import React from "react";
import { ReactComponent as IconSupport } from "@assets/Icons/IconSupport.svg";
import { ProductInternalName } from "../productConfig";
import { canSeeChangelog } from "@shared/config/featureFlags";
import WhatsNewCard from "./WhatsNewCard";
import {
  COLLAPSED_SQUARE,
  FOCUS_RING,
  FOOTER_PADDING,
  FOOTER_STACK,
  LABEL_CLAMP,
  ROW_GAP,
  ROW_ICON,
  ROW_ICON_STANDALONE,
  ROW_PADDING,
  ROW_RADIUS,
  ROW_TEXT,
} from "../sideMenuDensity";

export type SideMenuFooterBarProps = {
  isExpanded: boolean;
  isMobileView: boolean;
  internalName: string;
  gitbookToken: string | null | undefined;
  /** Decides the changelog row — see `canSeeChangelog`. */
  tenantType: string;
  /** Opens Technical Support. */
  onOpenHelp: () => void;
};

const SideMenuFooterBar: React.FC<SideMenuFooterBarProps> = ({
  isExpanded,
  isMobileView,
  internalName,
  gitbookToken,
  tenantType,
  onOpenHelp,
}) => {
  const showWide = isExpanded || isMobileView;

  /** Shared geometry for every footer row, wide or collapsed (CTX-242 scale). */
  const rowGeometry = showWide
    ? `w-full justify-start ${ROW_PADDING}`
    : `${COLLAPSED_SQUARE} justify-center`;

  /* Wide, the icon sits beside a label and takes the type size; collapsed, it is
     the button's only content — same rule as the nav rows. */
  const rowIcon = showWide ? ROW_ICON : ROW_ICON_STANDALONE;

  const rowClass = `
    group flex items-center ${ROW_GAP}
    ${ROW_RADIUS}
    text-semantic-text-secondary
    transition-all duration-200 motion-reduce:transition-none
    ${FOCUS_RING}
    hover:bg-semantic-state-active hover:text-semantic-text-brand
    ${rowGeometry}
  `;

  return (
    <div
      className={`
        ${FOOTER_PADDING}
        flex
        flex-col
        items-center
        ${FOOTER_STACK}
        border-t
        border-solid
        border-semantic-border-secondary
      `}
    >
      {/* "See what's new" leads the stack: it is the only footer row that can
          carry news, so it has to sit where the eye lands first rather than
          under two rows that never change. The flag gate is here, not inside
          the card, so the changelog feed is not fetched at all where the
          feature is hidden. */}
      {canSeeChangelog(tenantType) && (
        <WhatsNewCard
          showWide={showWide}
          rowClass={rowClass}
          rowIcon={rowIcon}
        />
      )}

      {/* Help — Technical Support */}
      <button
        type="button"
        onClick={onOpenHelp}
        title="Technical Support"
        aria-label="Technical Support"
        className={rowClass}
      >
        <IconSupport className={`${rowIcon} flex-shrink-0`} />
        {/* LABEL_CLAMP, not just ROW_TEXT: without its `leading-tight` the label
            inherits a normal line-height and the footer row stands taller than a
            nav row, which is what made the footer look misaligned with the rail.
            Same rule as a nav label, so it also wraps rather than truncating. */}
        {showWide && (
          <span className={`flex-1 text-left ${ROW_TEXT} ${LABEL_CLAMP} font-medium`}>
            Technical Support
          </span>
        )}
      </button>

      {internalName === ProductInternalName.CtxReflectorV2 && (
        <button
          type="button"
          onClick={() => {
            const base =
              process.env.REACT_APP_GITBOOK_URL || "https://docs.transitxchange.cloud";
            const url = gitbookToken ? `${base}?jwt_token=${gitbookToken}` : base;
            window.open(url, "_blank", "noopener,noreferrer");
          }}
          title="Documentation"
          className={`
            group flex items-center ${ROW_GAP}
            ${ROW_RADIUS}
            border border-semantic-border-secondary
            bg-semantic-bg-secondary
            hover:bg-semantic-bg-tertiary hover:border-semantic-border-primary
            transition-all duration-200 motion-reduce:transition-none
            ${FOCUS_RING}
            ${rowGeometry}
          `}
        >
          <svg
            className={`${rowIcon} flex-shrink-0 text-semantic-text-secondary transition-colors duration-200`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25"
            />
          </svg>
          {showWide && (
            <>
              <span
                className={`flex-1 text-left ${ROW_TEXT} ${LABEL_CLAMP} font-medium text-semantic-text-primary transition-colors duration-200`}
              >
                Documentation
              </span>
              <svg
                className={`${ROW_ICON} flex-shrink-0 text-semantic-text-secondary transition-colors duration-200`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                />
              </svg>
            </>
          )}
        </button>
      )}

    </div>
  );
};

export default SideMenuFooterBar;
